import { useEffect } from 'react';
import { useEditor } from 'tldraw';
import { useBoardAiContext } from '../context/BoardAiContext';
import { useBoardStore } from '../store/useBoardStore';
import { extractTextShapeContent } from '../utils/canvasTextContent';
import {
  buildPasteCanvasEvidence,
  CANVAS_EVIDENCE_SOURCE,
  isTypedCanvasTextShape,
  needsIntentSummary,
  shapeAlreadyDigested,
} from '../utils/pasteCanvasEvidence';
import { mergeAiCostIntoMeta, normalizeAiCost } from '../utils/aiCostFormat';
import { digestDeleteListenersSuppressed } from '../utils/boardConstraintsDigest';
import { postBoardAiEnrichClipboard } from '../api/boardAiApi';

/** Pause after the last store change / edit-end before enrich + pin-case. */
const TEXT_DIGEST_DEBOUNCE_MS = 800;
const TEXT_AI_ENRICH_THROTTLE_MS = 30_000;

/**
 * Enrich typed tldraw text/note shapes and classify them against Do-next blockers.
 * Short text → done analysis directly. Long text → intentSummary, then analysis.
 * Lives inside the editor tree (not onMount) so HMR rebinds the listener.
 */
export default function TextShapeDigest() {
  const editor = useEditor();
  const { hashtag, analyzePinAdded, enqueueDigestDelete } = useBoardAiContext();
  const canEdit = useBoardStore(s => Boolean(s.canEdit || s.isOwner));

  useEffect(() => {
    if (!editor || !canEdit || !hashtag) return undefined;

    const timers = new Map();
    const committedIds = new Set();
    const lastCommittedText = new Map();
    const lastAiEnrichAt = new Map();
    const inFlight = new Set();

    async function enrichAndCommitTextShape(shapeId) {
      if (inFlight.has(shapeId)) return;
      const shape = editor.getShape(shapeId);
      if (!shape || !isTypedCanvasTextShape(shape)) return;

      const rawText = extractTextShapeContent(shape);
      if (!rawText) {
        if (editor.getEditingShapeId?.() === shapeId) {
          schedule(shapeId);
        }
        return;
      }
      if (
        lastCommittedText.get(shapeId) === rawText
        && shapeAlreadyDigested(shape)
      ) {
        return;
      }

      inFlight.add(shapeId);
      try {
        const now = Date.now();
        const canCallAi = now - (lastAiEnrichAt.get(shapeId) || 0) >= TEXT_AI_ENRICH_THROTTLE_MS;
        const shouldSummarize = needsIntentSummary(rawText);
        if (shouldSummarize && canCallAi) {
          useBoardStore.getState().setObjectProcessing([{ shapeId }], 'enriching');
        }

        let intentSummary = String(shape.meta?.intentSummary || '').trim();
        let aiEnriched = Boolean(shouldSummarize && intentSummary);
        let enrichCost = null;

        if (shouldSummarize && canCallAi) {
          try {
            const enriched = await postBoardAiEnrichClipboard(hashtag, {
              kind: 'text',
              text: rawText,
            });
            const newSummary = String(enriched?.intentSummary || '').trim();
            if (newSummary) {
              intentSummary = newSummary;
              aiEnriched = true;
              lastAiEnrichAt.set(shapeId, Date.now());
              enrichCost = normalizeAiCost(enriched?.aiCost);
            }
          } catch (err) {
            console.warn('[text-digest] AI enrich failed', err?.message || err);
          }
        } else if (!shouldSummarize) {
          intentSummary = rawText;
          aiEnriched = false;
        }

        if (!intentSummary) intentSummary = rawText.slice(0, 300);

        const fresh = editor.getShape(shapeId);
        if (!fresh) return;
        editor.updateShape({
          id: shapeId,
          type: fresh.type,
          meta: {
            ...fresh.meta,
            intentSummary,
            source: CANVAS_EVIDENCE_SOURCE.TLDRAW_TEXT_TOOL,
            ...mergeAiCostIntoMeta(fresh.meta || {}, enrichCost),
          },
        });

        const evidence = buildPasteCanvasEvidence({
          shapeId,
          kind: 'text',
          rawText,
          intentSummary,
          source: CANVAS_EVIDENCE_SOURCE.TLDRAW_TEXT_TOOL,
          aiEnriched,
        });
        if (!evidence) return;
        analyzePinAdded?.({ canvasEvidence: evidence, placePending: false });
        committedIds.add(shapeId);
        lastCommittedText.set(shapeId, rawText);
      } finally {
        inFlight.delete(shapeId);
        useBoardStore.getState().clearObjectProcessing(
          [{ shapeId }],
          { onlyPhase: 'enriching' }
        );
      }
    }

    function schedule(shapeId, { isUpdate = false, delayMs = TEXT_DIGEST_DEBOUNCE_MS } = {}) {
      clearTimeout(timers.get(shapeId));
      timers.set(
        shapeId,
        setTimeout(async () => {
          timers.delete(shapeId);
          if (isUpdate && committedIds.has(shapeId)) {
            enqueueDigestDelete?.([], [shapeId]);
            committedIds.delete(shapeId);
          }
          await enrichAndCommitTextShape(shapeId);
        }, delayMs)
      );
    }

    function considerShape(shape, {
      delayMs = TEXT_DIGEST_DEBOUNCE_MS,
      skipIfClassified = false,
    } = {}) {
      if (!shape || !isTypedCanvasTextShape(shape)) return;
      const text = extractTextShapeContent(shape);
      if (!text) return;
      if (skipIfClassified && shapeAlreadyDigested(shape)) {
        committedIds.add(shape.id);
        lastCommittedText.set(shape.id, text);
        return;
      }
      schedule(shape.id, {
        isUpdate: committedIds.has(shape.id),
        delayMs,
      });
    }

    const unlistenDoc = editor.store.listen(
      (entry) => {
        const changes = entry?.changes || {};
        for (const record of Object.values(changes.removed || {})) {
          if (record?.typeName !== 'shape') continue;
          clearTimeout(timers.get(record.id));
          timers.delete(record.id);
          if (committedIds.has(record.id)) {
            if (!digestDeleteListenersSuppressed.active) {
              enqueueDigestDelete?.([], [record.id]);
            }
            committedIds.delete(record.id);
          }
          lastCommittedText.delete(record.id);
        }
        for (const record of Object.values(changes.added || {})) {
          if (record?.typeName !== 'shape') continue;
          considerShape(record, { skipIfClassified: true });
        }
        for (const tuple of Object.values(changes.updated || {})) {
          const prev = Array.isArray(tuple) ? tuple[0] : null;
          const next = Array.isArray(tuple) ? tuple[1] : tuple;
          if (next?.typeName !== 'shape') continue;
          if (!isTypedCanvasTextShape(next)) continue;
          const prevText = extractTextShapeContent(prev);
          const nextText = extractTextShapeContent(next);
          if (prevText === nextText) continue;
          considerShape(next);
        }
      },
      { scope: 'document' }
    );

    let prevEditingId = editor.getEditingShapeId?.() || null;
    const editingTextAtStart = new Map();
    const unlistenSession = editor.store.listen(
      () => {
        const nextId = editor.getEditingShapeId?.() || null;
        if (prevEditingId === nextId) return;
        const endedId = prevEditingId;
        if (nextId) {
          editingTextAtStart.set(
            nextId,
            extractTextShapeContent(editor.getShape(nextId))
          );
        }
        prevEditingId = nextId;
        if (!endedId) return;
        const ended = editor.getShape(endedId);
        const startText = editingTextAtStart.get(endedId);
        editingTextAtStart.delete(endedId);
        if (extractTextShapeContent(ended) === startText) return;
        considerShape(ended, { delayMs: 150 });
      },
      { scope: 'session' }
    );

    function scanUncommitted() {
      let queued = 0;
      for (const shape of editor.getCurrentPageShapes() || []) {
        if (!isTypedCanvasTextShape(shape)) continue;
        if (shape.meta?.intentSummary || shape.meta?.doneKey || shape.meta?.partialKey) continue;
        if (!extractTextShapeContent(shape)) continue;
        considerShape(shape, { delayMs: 200 + queued * 150 });
        queued += 1;
        if (queued >= 8) break;
      }
    }

    const scanSoon = setTimeout(scanUncommitted, 250);
    const scanAgain = setTimeout(scanUncommitted, 1800);

    return () => {
      unlistenDoc();
      unlistenSession();
      clearTimeout(scanSoon);
      clearTimeout(scanAgain);
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [editor, canEdit, hashtag, analyzePinAdded, enqueueDigestDelete]);

  return null;
}
